/**
 * Clue Menu Component
 *
 * Handles the clue/definition input panel.
 * Migrated from the legacy MeghMenu constructor function in olaszmodra.js.
 */

import type { Cell } from '../core/Cell';

/** Valid characters pattern for cell values (matches legacy g_joKarakterekMezo) */
const VALID_CELL_CHARS = /^[a-zA-ZáÁéÉíÍúÚüÜűŰóÓöÖőŐ]$/;

/**
 * Configuration for ClueMenu
 */
export interface ClueMenuConfig {
    /** Get direction (true = horizontal, false = vertical) */
    getIsHorizontal: () => boolean;
    /** Set direction */
    setIsHorizontal: (value: boolean) => void;
    /** Get image path for icons */
    getImagePath: () => string;
    /** Get the current cell */
    getCurrentCell: () => Cell | null;
    /** Get the first cell in selection (for clue) */
    getFirstCell: () => Cell | null;
    /** Get full selection array */
    getSelection: () => number[];
    /** Get the current cell ID */
    getCurrentCellId: () => number;
    /** Get a cell by ID */
    getCellById: (cellId: number) => Cell | null;
    /** Check if in bridge channel */
    isInChannel: () => boolean;
    /** Callback when clue changes */
    onClueChange?: (clue: string) => void;
    /** Callback when expression is restored */
    onExpressionRestore?: (expression: string) => void;
}

/**
 * Clue Menu Component
 *
 * Manages the clue/definition input area with support for
 * horizontal, vertical, and bridge clues.
 */
export class ClueMenu {
    private readonly _config: ClueMenuConfig;
    private _cell: Cell | null = null;
    private _firstCell: Cell | null = null;

    constructor(config: ClueMenuConfig) {
        this._config = config;
    }

    // ============================================================================
    // State Management
    // ============================================================================

    /**
     * Set the current direction
     */
    setDirection(isHorizontal: boolean): void {
        this._config.setIsHorizontal(isHorizontal);
    }

    /**
     * Update cell references
     */
    updateCells(): void {
        this._cell = this._config.getCurrentCell();
        this._firstCell = this._config.getFirstCell();
    }

    /**
     * Enable expression error state for current direction
     */
    enableExpressionError(expression: string): void {
        if (!this._firstCell) return;

        const isHorizontal = this._config.getIsHorizontal();
        const isInChannel = this._config.isInChannel();

        if (isHorizontal) {
            if (isInChannel && this._firstCell.hasVerticalBackClue) {
                if (this._firstCell.verticalBackExpression !== expression) {
                    this._firstCell.enableVerticalBackExpressionError();
                } else {
                    this._firstCell.disableVerticalBackExpressionError();
                }
            } else if (this._firstCell.hasVerticalClue) {
                if (this._firstCell.verticalExpression !== expression) {
                    this._firstCell.enableVerticalExpressionError();
                } else {
                    this._firstCell.disableVerticalExpressionError();
                }
            }
        } else {
            if (isInChannel && this._firstCell.hasHorizontalBackClue) {
                if (this._firstCell.horizontalBackExpression !== expression) {
                    this._firstCell.enableHorizontalBackExpressionError();
                } else {
                    this._firstCell.disableHorizontalBackExpressionError();
                }
            } else if (this._firstCell.hasHorizontalClue) {
                if (this._firstCell.horizontalExpression !== expression) {
                    this._firstCell.enableHorizontalExpressionError();
                } else {
                    this._firstCell.disableHorizontalExpressionError();
                }
            }
        }
    }

    /**
     * Disable expression error state for current direction
     */
    disableExpressionError(): void {
        if (!this._firstCell) return;

        const isHorizontal = this._config.getIsHorizontal();
        const isInChannel = this._config.isInChannel();

        if (isHorizontal) {
            if (isInChannel) {
                this._firstCell.disableVerticalBackExpressionError();
            } else {
                this._firstCell.disableVerticalExpressionError();
            }
        } else {
            if (isInChannel) {
                this._firstCell.disableHorizontalBackExpressionError();
            } else {
                this._firstCell.disableHorizontalExpressionError();
            }
        }
    }

    // ============================================================================
    // Event Handlers
    // ============================================================================

    /**
     * Handle keyup event on clue textarea
     */
    handleKeyUp = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            const textarea = event.target as HTMLTextAreaElement;
            this._config.onClueChange?.(textarea.value);
        }
    };

    /**
     * Handle expression restore click
     */
    handleExpressionRestore = (expression: string): void => {
        this._config.onExpressionRestore?.(expression);
    };

    // ============================================================================
    // Rendering
    // ============================================================================

    /**
     * Get the full selection array including the current cell ID, sorted
     */
    private getFullSelection(): number[] {
        const selection = this._config.getSelection().slice();
        const currentCellId = this._config.getCurrentCellId();
        if (!selection.includes(currentCellId)) {
            selection.push(currentCellId);
        }
        selection.sort((a, b) => a - b);
        return selection;
    }

    /**
     * Get the first cell ID from the selection
     */
    private getFirstCellId(): number {
        const fullSelection = this.getFullSelection();
        return fullSelection[0];
    }

    /**
     * Build the expression string from cell values in the selection
     */
    private getExpressionFromCells(): string {
        const fullSelection = this.getFullSelection();
        let expression = '';

        for (const cellId of fullSelection) {
            const cell = this._config.getCellById(cellId);
            const value = cell?.value ?? '';

            if (typeof value === 'string' && VALID_CELL_CHARS.test(value)) {
                expression += value;
            } else {
                expression += '_';
            }
        }

        return expression;
    }

    /**
     * Update the selected expression display in the header
     * This is the equivalent of updateKijeloltKifejezes in the legacy code
     */
    updateSelectedExpression(): void {
        const headerEl = document.getElementById('rejtveny_fejlec');
        if (!headerEl) return;

        const firstCellId = this.getFirstCellId();
        const firstCell = this._config.getCellById(firstCellId);
        if (!firstCell) {
            headerEl.innerHTML = '';
            return;
        }

        const currentExpression = this.getExpressionFromCells();
        const isHorizontal = this._config.getIsHorizontal();
        const isInChannel = this._config.isInChannel();

        // Get the stored expression to compare against
        let storedExpression: string;

        if (isHorizontal) {
            if (isInChannel && firstCell.hasVerticalBackClue) {
                // Horizontal direction in bridge channel - use vertical back expression
                storedExpression = firstCell.hasVerticalBackExpressionError
                    ? (firstCell.verticalBackExpression ?? currentExpression)
                    : currentExpression;
            } else if (firstCell.hasVerticalClue) {
                // Horizontal direction - use vertical expression
                storedExpression = firstCell.hasVerticalExpressionError
                    ? (firstCell.verticalExpression ?? currentExpression)
                    : currentExpression;
            } else {
                storedExpression = currentExpression;
            }
        } else {
            if (isInChannel && firstCell.hasHorizontalBackClue) {
                // Vertical direction in bridge channel - use horizontal back expression
                storedExpression = firstCell.hasHorizontalBackExpressionError
                    ? (firstCell.horizontalBackExpression ?? currentExpression)
                    : currentExpression;
            } else if (firstCell.hasHorizontalClue) {
                // Vertical direction - use horizontal expression
                storedExpression = firstCell.hasHorizontalExpressionError
                    ? (firstCell.horizontalExpression ?? currentExpression)
                    : currentExpression;
            } else {
                storedExpression = currentExpression;
            }
        }

        // Build the display HTML with character-by-character comparison
        let html = '';
        for (let i = 0; i < currentExpression.length; i++) {
            const currentChar = currentExpression[i];
            const storedChar = storedExpression[i];

            if (currentChar === storedChar) {
                html += `<b>${currentChar}</b>`;
            } else {
                html += `<b style="color:rgb(200, 0, 0);">${currentChar}</b>`;
            }
        }

        headerEl.innerHTML = html;
    }

    /**
     * Render the clue menu HTML
     */
    render(): string {
        this.updateCells();
        this.updateSelectedExpression();

        if (!this._firstCell || !this._cell) {
            return '';
        }

        // Don't show for black or image cells
        if (this._firstCell.isBlack || this._firstCell.isImage) {
            return '';
        }

        // Need at least 2 cells in selection
        const selection = this._config.getSelection();
        if (selection.length < 1) {
            return '';
        }

        const isHorizontal = this._config.getIsHorizontal();
        const isInChannel = this._config.isInChannel();
        const imgPath = this._config.getImagePath();

        // Determine which type of clue to show
        if (isHorizontal && isInChannel) {
            // Horizontal bridge clue
            return this.renderBridgeClue(
                'Vízszintes híd meghatározás',
                'verticalBack',
                imgPath,
            );
        } else if (!isHorizontal && isInChannel) {
            // Vertical bridge clue
            return this.renderBridgeClue(
                'Függőleges híd meghatározás',
                'horizontalBack',
                imgPath,
            );
        } else if (isHorizontal) {
            // Standard horizontal clue
            return this.renderStandardClue(
                'Vízszintes meghatározás',
                'vertical',
                imgPath,
            );
        } else {
            // Standard vertical clue
            return this.renderStandardClue(
                'Függőleges meghatározás',
                'horizontal',
                imgPath,
            );
        }
    }

    /**
     * Get the first cell in the full sorted selection.
     * This is the cell that holds the clue/expression for the word.
     */
    private getFirstCellInSelection(): Cell | null {
        const firstCellId = this.getFirstCellId();
        return this._config.getCellById(firstCellId);
    }

    /**
     * Render standard clue input
     */
    private renderStandardClue(
        label: string,
        direction: 'vertical' | 'horizontal',
        imgPath: string,
    ): string {
        // Use the first cell from the full sorted selection (like legacy getTeljesTomb[0])
        const firstCell = this.getFirstCellInSelection();
        if (!firstCell) return '';

        const hasError =
            direction === 'vertical'
                ? firstCell.hasVerticalExpressionError
                : firstCell.hasHorizontalExpressionError;

        const expression =
            direction === 'vertical'
                ? firstCell.verticalExpression
                : firstCell.horizontalExpression;

        let html = `<label for="textareaMeghatarozas">${label}</label>`;
        html +=
            '<textarea name="textareaMeghatarozas" id="textareaMeghatarozas" rows="5"></textarea>';

        if (hasError && expression) {
            html += `<p>Megváltozott kifejezés: <a href="javascript:setKifejezes('${expression}');" class="figyelmeztetes" desc="Eredeti: ${expression}"><img src="${imgPath}warning.png"></a></p>`;
        }

        return `<form><div class="input-block">${html}</div></form>`;
    }

    /**
     * Render bridge clue input
     */
    private renderBridgeClue(
        label: string,
        direction: 'verticalBack' | 'horizontalBack',
        imgPath: string,
    ): string {
        // Use the first cell from the full sorted selection (like legacy getTeljesTomb[0])
        const firstCell = this.getFirstCellInSelection();
        if (!firstCell) return '';

        const hasError =
            direction === 'verticalBack'
                ? firstCell.hasVerticalBackExpressionError
                : firstCell.hasHorizontalBackExpressionError;

        const expression =
            direction === 'verticalBack'
                ? firstCell.verticalBackExpression
                : firstCell.horizontalBackExpression;

        let html = `<label for="textareaMeghatarozas">${label}</label>`;
        html +=
            '<textarea name="textareaMeghatarozas" id="textareaMeghatarozas" rows="5"></textarea>';

        if (hasError && expression) {
            html += `<p>Megváltozott kifejezés: <a href="javascript:setKifejezes('${expression}');" class="figyelmeztetes" desc="Eredeti: ${expression}"><img src="${imgPath}warning.png"></a></p>`;
        }

        return `<form><div class="input-block">${html}</div></form>`;
    }

    /**
     * Render to string (for legacy compatibility)
     */
    toString(): string {
        return this.render();
    }

    /**
     * Update the menu in the DOM
     */
    update(containerId = 'meghatarozasMenu'): void {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = this.render();
            this.attachEvents();
        }
    }

    /**
     * Attach event handlers to the textarea
     */
    attachEvents(): void {
        const textarea = document.getElementById(
            'textareaMeghatarozas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.addEventListener('keyup', this.handleKeyUp);
        }
    }

    /**
     * Detach event handlers from the textarea
     */
    detachEvents(): void {
        const textarea = document.getElementById(
            'textareaMeghatarozas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.removeEventListener('keyup', this.handleKeyUp);
        }
    }

    /**
     * Set focus to the clue textarea
     */
    focus(): void {
        const textarea = document.getElementById(
            'textareaMeghatarozas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.focus();
        }
    }

    /**
     * Set the clue textarea value
     */
    setValue(value: string): void {
        const textarea = document.getElementById(
            'textareaMeghatarozas',
        ) as HTMLTextAreaElement;
        if (textarea) {
            textarea.value = value;
        }
    }

    /**
     * Get the clue textarea value
     */
    getValue(): string {
        const textarea = document.getElementById(
            'textareaMeghatarozas',
        ) as HTMLTextAreaElement;
        return textarea?.value ?? '';
    }
}
